import { type UseMutationOptions, useMutation } from "@tanstack/react-query";
import { ApiRequestError } from "./api";

// Mutations with an Idempotency-Key (docs/05): one key per user intent, reused when the request
// is retried after a network failure, so a retry never creates a duplicate on the server.

type WithKey<V> = { vars: V; key: string };
/** Mutations without input are called as `run()`. */
type Args<V> = undefined extends V ? [vars?: V] : [vars: V];

export function useApiMutation<V, R>(
  fn: (vars: V, idempotencyKey: string) => Promise<R>,
  options: Omit<UseMutationOptions<R, Error, WithKey<V>>, "mutationFn" | "retry"> = {},
) {
  const mutation = useMutation<R, Error, WithKey<V>>({
    ...options,
    mutationFn: ({ vars, key }) => fn(vars, key),
    // Retry only when the request may not have reached the server; never on an API answer.
    retry: (count, error) => !(error instanceof ApiRequestError) && count < 3,
  });
  return {
    ...mutation,
    run: (...args: Args<V>) => mutation.mutate({ vars: args[0] as V, key: crypto.randomUUID() }),
    runAsync: (...args: Args<V>) =>
      mutation.mutateAsync({ vars: args[0] as V, key: crypto.randomUUID() }),
  };
}

export const idempotencyHeaders = (key: string) => ({ headers: { "Idempotency-Key": key } });
